import { useCallback, useSyncExternalStore } from 'react';

import { useSessionStore } from '@/shared/api';

/**
 * Kleine schermvoorkeuren, op één plek.
 *
 * "Staat de detailbalk ingeklapt", "welke weergave stond aan": geen domeindata,
 * dus niet in de outbox en niet in de querycache. Ze horen bij deze gebruiker op
 * dit apparaat en leven in `localStorage`.
 *
 * Waarom een eigen store en niet gewoon `useState` per scherm: twee componenten
 * die dezelfde voorkeur lezen moeten hetzelfde zien, ook als de een hem wijzigt
 * terwijl de ander in beeld staat.
 *
 * `localStorage` kan gooien (privémodus, geblokkeerde site-data). Dan werkt alles
 * gewoon door met de standaardwaarde; een voorkeur die niet bewaard kan worden
 * is geen fout die de gebruiker hoeft te zien.
 */
export type PreferenceValue = boolean | number | string;

/**
 * `usePreference('x', false)` moet `boolean` teruggeven en niet `false`: anders
 * kun je hem nooit op `true` zetten.
 */
type Widen<T extends PreferenceValue> = T extends boolean
    ? boolean
    : T extends number
      ? number
      : string;

const memory = new Map<string, PreferenceValue>();
const listeners = new Set<() => void>();

function keyFor(name: string, userId: string): string {
    return `smartkeur:${name}:${userId}`;
}

function read<T extends PreferenceValue>(key: string, fallback: T): T {
    if (memory.has(key)) {
        return memory.get(key) as T;
    }

    try {
        const stored = window.localStorage.getItem(key);

        if (stored === null) {
            return fallback;
        }

        const value = parse(stored, fallback);

        memory.set(key, value);

        return value;
    } catch {
        return fallback;
    }
}

function parse<T extends PreferenceValue>(stored: string, fallback: T): T {
    if (typeof fallback === 'boolean') {
        return (stored === '1') as T;
    }

    if (typeof fallback === 'number') {
        const value = Number(stored);

        return (Number.isFinite(value) ? value : fallback) as T;
    }

    return stored as T;
}

function write(key: string, value: PreferenceValue): void {
    memory.set(key, value);

    try {
        window.localStorage.setItem(
            key,
            typeof value === 'boolean' ? (value ? '1' : '0') : String(value),
        );
    } catch {
        /* Niet kunnen bewaren mag het scherm niet in de weg zitten. */
    }

    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

/** Leest en schrijft één voorkeur, per ingelogde gebruiker gescheiden. */
export function usePreference<T extends PreferenceValue>(
    name: string,
    fallback: T,
): [Widen<T>, (next: Widen<T>) => void] {
    const userId = useSessionStore((state) => state.session?.user.uuid ?? 'anoniem');
    const storageKey = keyFor(name, userId);

    const value = useSyncExternalStore(
        subscribe,
        () => read(storageKey, fallback),
        () => fallback,
    );

    const update = useCallback((next: Widen<T>) => write(storageKey, next), [storageKey]);

    return [value as unknown as Widen<T>, update];
}

/**
 * Dezelfde voorkeur zetten buiten een component om.
 *
 * Voor een scherm dat een voorkeur klaarzet voor een ander scherm: het rapport
 * zet het onderdeel waar de opname moet openen, en navigeert er dan heen. De
 * gebruiker geef je mee omdat voorkeuren per ingelogde persoon gescheiden zijn -
 * `useSessionStore.getState().session?.user.uuid`.
 */
export function setPreference(name: string, userId: string, value: PreferenceValue): void {
    write(keyFor(name, userId), value);
}

/** Alleen voor tests: gooit de in-memory spiegel weg. */
export function resetPreferences(): void {
    memory.clear();
    listeners.forEach((listener) => listener());
}
